import React, { useEffect, useMemo, useState } from 'react';
import { CheckIcon } from 'lucide-react';
import { IAutomationVariable, AutomationVariableValue } from '@frontend/app/types/automations/condition/AutomationVariable';
import { ComboOperator, ComparisonOperator, MappedOperator } from '@frontend/app/types/automations/condition/Operator';
import { ConditionType } from '@frontend/app/types/automations/condition/Condition';
import { FieldType } from '@frontend/app/types/globalTypes';
import { GetNodeOptions_getNodeOptions_conditionOptions } from '@frontend/app/queries/types/GetNodeOptions';
import { convertToTitleCase } from '@frontend/app/utils/strings';
import {
  getAllowedAutomationVariables,
  getFieldsForGroup,
  getGroupsForVariable,
} from '@frontend/app/containers/Projects/AutomationConfig/utils/condition';
import { getOperatorOptions } from '@frontend/app/containers/Projects/AutomationConfig/utils';
import { isEmailAddressValid } from '@common';
import { isArray, isEmpty, isNil, isString } from 'lodash';
import { FormattedOption } from '@frontend/app/containers/Projects/AutomationConfig/types';

import { X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shadcn/components/ui/card';
import { Button } from '@/shadcn/components/ui/button';
import { Input } from '@/shadcn/components/ui/input';
import { Label } from '@/shadcn/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shadcn/components/ui/select';

import { AutomationNodeV2 } from '../types';

// Convert AutomationConditionV2 to metadata condition format
const convertToMetadataCondition = (condition: AutomationConditionV2) => {
  // Map MappedOperator to the limited set allowed in metadata
  const getMetadataOp = (op: MappedOperator): 'AND' | 'OR' | 'EQUALS' | 'NOT_EQUALS' | 'GREATER_THAN' | 'LESS_THAN' | 'CONTAINS' => {
    switch (op) {
      case ComparisonOperator.EQUALS:
        return 'EQUALS';
      case ComboOperator.NOT_EQUALS:
        return 'NOT_EQUALS';
      case ComparisonOperator.GREATER_THAN:
        return 'GREATER_THAN';
      case ComparisonOperator.LESS_THAN:
        return 'LESS_THAN';
      case ComparisonOperator.CONTAINS:
      case ComparisonOperator.HAS_VALUE:
      case ComparisonOperator.STARTS_WITH:
        return 'CONTAINS';
      default:
        return 'EQUALS'; // fallback
    }
  };

  return {
    type: 'COMPARISON' as const,
    op: getMetadataOp(condition.op),
    value: condition.value,
    variableId: condition.variableId,
    attribute: condition.attribute,
    locked: false,
  };
};

interface AutomationConditionV2 {
  type: ConditionType.COMPARISON;
  attribute: string;
  mappedOperator: MappedOperator;
  op: MappedOperator;
  value: AutomationVariableValue;
  variableId: string;
  locked: boolean;
  uuid: string;
}

interface ConditionFormV2Props {
  selectedNode: AutomationNodeV2;
  onUpdateNode: (nodeId: string, updates: Partial<AutomationNodeV2>) => void;
  onClose: () => void;
  automationVariables: IAutomationVariable[];
  conditionOptions: GetNodeOptions_getNodeOptions_conditionOptions[];
}

export const ConditionFormV2: React.FC<ConditionFormV2Props> = ({
  selectedNode,
  onUpdateNode,
  onClose,
  automationVariables,
  conditionOptions,
}) => {
  const formattedOptions = useMemo<FormattedOption[]>(() =>
    conditionOptions.map((option) => ({
      ...option,
      group: convertToTitleCase(option.group),
    })), [conditionOptions]
  );

  const [selectedAutomationVariable, setSelectedAutomationVariable] = useState<IAutomationVariable | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [groupsForVariable, setGroupsForVariable] = useState<string[]>([]);
  const [fieldsForGroup, setFieldsForGroup] = useState<FormattedOption[]>([]);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [operators, setOperators] = useState<Array<{ value: MappedOperator; operatorLabel: string }>>([]);
  const [selectedOperator, setSelectedOperator] = useState<MappedOperator | null>(null);
  const [value, setValue] = useState<AutomationVariableValue>(null);
  const [validationError, setValidationError] = useState<string>('');

  // Check if this is a new condition
  const currentCondition = selectedNode.metadata.condition as AutomationConditionV2 | null;
  const existingConditions = selectedNode.metadata.children || [];
  // If currentCondition is null OR has no attribute, we're adding new
  // Only if currentCondition has an attribute are we editing
  const isNewCondition = isNil(currentCondition) || isNil(currentCondition?.attribute);
  const selectedField = fieldsForGroup.find((field) => field.id === selectedFieldId);

  // Initialize form state
  useEffect(() => {
    if (formattedOptions.length > 0) {
      const allowedVars = getAllowedAutomationVariables(automationVariables);

      if (!isNewCondition && currentCondition?.attribute) {
        // Load existing condition
        const variableId = currentCondition.variableId;
        const selectedVar = allowedVars.find((variable) => variable.id === variableId);
        setSelectedAutomationVariable(selectedVar || null);

        const groups = getGroupsForVariable(selectedVar?.type, formattedOptions);
        setGroupsForVariable(groups);

        const group = formattedOptions.find((field) => field.id === currentCondition.attribute)?.group;
        setSelectedGroup(group || null);

        const fieldId = currentCondition.attribute;
        setSelectedFieldId(fieldId);

        const fields = getFieldsForGroup(group, formattedOptions, selectedVar?.type);
        setFieldsForGroup(fields);

        const selectedOp = (currentCondition.mappedOperator || currentCondition.op) as MappedOperator;
        setSelectedOperator(selectedOp);

        const fieldForGroup = fields.find((field) => field.id === currentCondition.attribute);
        const ops = getOperatorOptions(fieldForGroup?.attributeType, fieldForGroup?.choices);
        setOperators(ops);

        setValue(currentCondition.value);
      } else {
        // Initialize new condition
        const selectedVar = allowedVars[0];
        setSelectedAutomationVariable(selectedVar);

        const groups = getGroupsForVariable(selectedVar?.type, formattedOptions);
        setGroupsForVariable(groups);

        const group = groups[0];
        setSelectedGroup(group);

        const fields = getFieldsForGroup(group, formattedOptions, selectedVar?.type);
        setFieldsForGroup(fields);

        const field = fields[0];
        setSelectedFieldId(field?.id || null);

        const ops = getOperatorOptions(field?.attributeType, field?.choices);
        setOperators(ops);

        const selectedOp = ops[0]?.value as MappedOperator;
        setSelectedOperator(selectedOp);

        // Don't set a default value for new conditions - let user select
        setValue(null);
      }
    }
  }, [automationVariables, formattedOptions, currentCondition, isNewCondition]);

  const onChangeGroup = (group: string) => {
    setSelectedGroup(group);

    const fields = getFieldsForGroup(group, formattedOptions, selectedAutomationVariable?.type);
    setFieldsForGroup(fields);

    const field = fields[0];
    setSelectedFieldId(field?.id || null);

    const ops = getOperatorOptions(field?.attributeType, field?.choices);
    setOperators(ops);

    const selectedOp = ops[0]?.value as MappedOperator;
    setSelectedOperator(selectedOp);

            // Don't set a default value - let user select
        setValue(null);
    setValidationError('');
  };

  const onChangeField = (fieldId: string) => {
    const field = fieldsForGroup.find((f) => f.id === fieldId);
    if (!field) return;

    setSelectedFieldId(fieldId);

    const ops = getOperatorOptions(field.attributeType, field.choices);
    setOperators(ops);

    const selectedOp = ops[0]?.value as MappedOperator;
    setSelectedOperator(selectedOp);

            // Don't set a default value - let user select
        setValue(null);
    setValidationError('');
  };

  const onChangeOperator = (operator: MappedOperator) => {
    setSelectedOperator(operator);
    if (operator === ComparisonOperator.HAS_VALUE || operator === ComboOperator.IS_NULL) {
      setValue(null);
    }
    setValidationError('');
  };

  const validateCondition = () => {
    const invalidValueError = 'Please enter a valid value';
    const noValueError = 'Please enter a value';
    const noVariableError = 'Please select a variable';
    const negativeNumberError = 'Please enter a positive number';
    const betweenValueError = 'Please enter a valid range';
    const invalidEmailFormatError = 'Please enter a valid email';
    const noError = '';
    let errorString = noError;

    if (isNil(selectedFieldId)) {
      errorString = noVariableError;
    }

    if (selectedOperator && errorString === noError) {
      switch (selectedOperator) {
        case ComparisonOperator.CONTAINS:
        case ComboOperator.NOT_CONTAINS:
          if (isNil(value)) errorString = noValueError;
          if (value === '') errorString = noValueError;
          if (isArray(value) && value.length === 0) errorString = noValueError;
          break;
        case ComparisonOperator.HAS_VALUE:
        case ComboOperator.IS_NULL:
          // These operators don't need a value
          break;
        case ComparisonOperator.EQUALS:
        case ComboOperator.NOT_EQUALS:
          if (isNil(value)) errorString = noValueError;
          if (value === '') errorString = noValueError;
          if (typeof value === 'string' && value !== '' && selectedField?.attributeType === FieldType.EMAIL && !isEmailAddressValid(value)) {
            errorString = invalidEmailFormatError;
          }
          if (isArray(value) && isEmpty(value)) errorString = noValueError;
          break;
        case ComparisonOperator.IN_THE_LAST:
        case ComparisonOperator.IN_THE_NEXT:
        case ComparisonOperator.MORE_THAN_AGO:
        case ComboOperator.NOT_IN_THE_LAST:
        case ComboOperator.NOT_IN_THE_NEXT:
        case ComboOperator.NOT_MORE_THAN_AGO:
          if (isNil(value)) errorString = noValueError;
          if (Number(value) < 0) errorString = negativeNumberError;
          break;
        case ComparisonOperator.STARTS_WITH:
        case ComparisonOperator.ENDS_WITH:
        case ComboOperator.NOT_STARTS_WITH:
        case ComboOperator.NOT_ENDS_WITH:
          if (isNil(value)) errorString = noValueError;
          if (!isString(value)) errorString = noValueError;
          if (value === '') errorString = noValueError;
          break;
        case ComparisonOperator.GREATER_THAN:
        case ComparisonOperator.GREATER_THAN_EQUALS:
        case ComparisonOperator.LESS_THAN:
        case ComparisonOperator.LESS_THAN_EQUALS:
          if (isNil(value)) errorString = noValueError;
          break;
        case ComboOperator.BETWEEN:
          if (isNil(value)) errorString = noValueError;
          if (!isArray(value)) errorString = invalidValueError;
          if (isArray(value) && value.length !== 2) errorString = invalidValueError;
          if (isArray(value) && value.length === 2 && value[0] > value[1]) errorString = betweenValueError;
          if (isArray(value) && typeof value[0] !== typeof value[1]) errorString = invalidValueError;
          break;
      }
    }

    setValidationError(errorString);
    return errorString === noError;
  };

  const handleDone = () => {
    if (!validateCondition()) return;

    const newCondition: AutomationConditionV2 = {
      type: ConditionType.COMPARISON,
      attribute: selectedFieldId!,
      mappedOperator: selectedOperator!,
      op: selectedOperator!,
      value,
      variableId: selectedAutomationVariable?.id || '',
      locked: false,
      uuid: currentCondition?.uuid || `condition-${Date.now()}`,
    };

    if (isNewCondition) {
      // Adding a new condition
      const existingChildren = selectedNode.metadata.children || [];
      const updatedChildren = [...existingChildren, newCondition];

      onUpdateNode(selectedNode.id, {
        metadata: {
          ...selectedNode.metadata,
          children: updatedChildren,
          // Also save as single condition for V2 compatibility
          condition: convertToMetadataCondition(newCondition),
        },
      });
    } else {
      // Editing existing condition
      const existingChildren = selectedNode.metadata.children || [];
      const updatedChildren = existingChildren.map((child: AutomationConditionV2) =>
        (child.uuid === currentCondition?.uuid ? newCondition : child)
      );

      onUpdateNode(selectedNode.id, {
        metadata: {
          ...selectedNode.metadata,
          children: updatedChildren,
          // Update single condition if it matches
          condition: currentCondition?.uuid === (selectedNode.metadata.condition as AutomationConditionV2 | null)?.uuid ? convertToMetadataCondition(newCondition) : selectedNode.metadata.condition,
        },
      });
    }

    onClose();
  };

  const renderValueInput = () => {
    // Don't show value input for operators that don't need values
    if (!selectedOperator || selectedOperator === ComparisonOperator.HAS_VALUE || selectedOperator === ComboOperator.IS_NULL) {
      return null;
    }

    if (!selectedField) {
      return null;
    }

    // Boolean fields - show Yes/No dropdown
    if (selectedField.attributeType === FieldType.BOOLEAN) {
      const operators: MappedOperator[] = [ComparisonOperator.EQUALS, ComboOperator.NOT_EQUALS];
      if (selectedOperator && operators.includes(selectedOperator)) {
        // Only show a value if it's actually a boolean, otherwise show placeholder
        const currentBoolValue = typeof value === 'boolean' ? value.toString() : '';
        return (
          <Select value={currentBoolValue} onValueChange={(val) => setValue(val === 'true')}>
            <SelectTrigger>
              <SelectValue placeholder="Select Yes or No..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">Yes</SelectItem>
              <SelectItem value="false">No</SelectItem>
            </SelectContent>
          </Select>
        );
      }
    }

    // Text fields with predefined choices - show dropdown
    if (selectedField.attributeType === FieldType.TEXT && selectedField.choices && selectedField.choices.length > 0) {
      const operators: MappedOperator[] = [ComparisonOperator.EQUALS, ComboOperator.NOT_EQUALS, ComparisonOperator.CONTAINS, ComboOperator.NOT_CONTAINS];
      if (selectedOperator && operators.includes(selectedOperator)) {
        // Only show a value if it's actually set, otherwise show placeholder
        const currentValue = value !== null && value !== undefined ? value.toString() : '';
        return (
          <Select value={currentValue} onValueChange={setValue}>
            <SelectTrigger>
              <SelectValue placeholder="Select a value..." />
            </SelectTrigger>
            <SelectContent>
              {selectedField.choices.map((choice) => (
                <SelectItem key={choice.value} value={choice.value.toString()}>
                  {choice.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }
    }

    // Array fields - show multi-select dropdown
    if (selectedField.attributeType === FieldType.ARRAY && selectedField.choices && selectedField.choices.length > 0) {
      const operators: MappedOperator[] = [ComparisonOperator.CONTAINS, ComboOperator.NOT_CONTAINS, ComparisonOperator.EQUALS, ComboOperator.NOT_EQUALS];
      if (selectedOperator && operators.includes(selectedOperator)) {
        // For now, use single select until we implement proper multi-select
        // Only show a value if it's actually set, otherwise show placeholder
        const currentValue = Array.isArray(value) && value.length > 0 ? value[0]?.toString() || '' :
                           (value !== null && value !== undefined ? value.toString() : '');
        return (
          <Select value={currentValue} onValueChange={(val) => setValue([val])}>
            <SelectTrigger>
              <SelectValue placeholder="Select a value..." />
            </SelectTrigger>
            <SelectContent>
              {selectedField.choices.map((choice) => (
                <SelectItem key={choice.value} value={choice.value.toString()}>
                  {choice.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }
    }

    // Number fields - show number input
    if ([FieldType.NUMBER, FieldType.PERCENTAGE].includes(selectedField.attributeType)) {
      const operators: MappedOperator[] = [
        ComparisonOperator.EQUALS,
        ComboOperator.NOT_EQUALS,
        ComparisonOperator.GREATER_THAN,
        ComparisonOperator.GREATER_THAN_EQUALS,
        ComparisonOperator.LESS_THAN,
        ComparisonOperator.LESS_THAN_EQUALS,
        ComparisonOperator.IN_THE_LAST,
        ComparisonOperator.IN_THE_NEXT,
        ComparisonOperator.MORE_THAN_AGO,
        ComboOperator.NOT_IN_THE_LAST,
        ComboOperator.NOT_IN_THE_NEXT,
        ComboOperator.NOT_MORE_THAN_AGO,
      ];
      if (selectedOperator && operators.includes(selectedOperator)) {
        return (
          <Input
            type="number"
            value={value?.toString() || ''}
            onChange={(e) => {
              const value = parseFloat(e.target.value);
              setValue(isNaN(value) ? 0 : value);
            }}
            placeholder="Enter number..."
          />
        );
      }
    }

    // Date fields - show date input (simplified for now)
    if (selectedField.attributeType === FieldType.DATE) {
      const dateOperators: MappedOperator[] = [
        ComparisonOperator.EQUALS,
        ComboOperator.NOT_EQUALS,
        ComparisonOperator.GREATER_THAN,
        ComparisonOperator.LESS_THAN,
      ];
      const numberOperators: MappedOperator[] = [
        ComparisonOperator.IN_THE_LAST,
        ComparisonOperator.IN_THE_NEXT,
        ComparisonOperator.MORE_THAN_AGO,
        ComboOperator.NOT_IN_THE_LAST,
        ComboOperator.NOT_IN_THE_NEXT,
        ComboOperator.NOT_MORE_THAN_AGO,
      ];

      if (selectedOperator && dateOperators.includes(selectedOperator)) {
        return (
          <Input
            type="date"
            value={value?.toString() || ''}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Select date..."
          />
        );
      } else if (selectedOperator && numberOperators.includes(selectedOperator)) {
        return (
          <Input
            type="number"
            value={value?.toString() || ''}
            onChange={(e) => setValue(parseFloat(e.target.value) || 0)}
            placeholder="Enter number of days..."
          />
        );
      }
    }

    // Text fields without choices - show text input
    if ([FieldType.TEXT, FieldType.EMAIL].includes(selectedField.attributeType)) {
      const operators: MappedOperator[] = [
        ComparisonOperator.EQUALS,
        ComboOperator.NOT_EQUALS,
        ComparisonOperator.CONTAINS,
        ComboOperator.NOT_CONTAINS,
        ComparisonOperator.STARTS_WITH,
        ComparisonOperator.ENDS_WITH,
        ComboOperator.NOT_STARTS_WITH,
        ComboOperator.NOT_ENDS_WITH,
      ];
      if (selectedOperator && operators.includes(selectedOperator) && (!selectedField.choices || selectedField.choices.length === 0)) {
        return (
          <Input
            type={selectedField.attributeType === FieldType.EMAIL ? 'email' : 'text'}
            value={value?.toString() || ''}
            onChange={(e) => setValue(e.target.value)}
            placeholder={selectedField.attributeType === FieldType.EMAIL ? 'Enter email...' : 'Enter text...'}
          />
        );
      }
    }

    // Default fallback - basic text input
    return (
      <Input
        value={value?.toString() || ''}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Enter value..."
      />
    );
  };

  return (
    <Card className="h-full rounded-none border-r-0 border-t-0 border-b-0">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckIcon className="h-5 w-5" />
            <CardTitle className="text-base">{isNewCondition ? 'Add Condition' : 'Edit Condition'}</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-6 w-6 p-0"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
        <p className="text-sm text-grey-5">
          Conditions are rules that must be true in order to move to the next step.
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Show a helpful message when there are no conditions and we're not editing */}
        {existingConditions.length === 0 && isNewCondition && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
            <div className="text-sm text-blue-800 font-medium">Create Your First Condition</div>
            <div className="text-xs text-blue-600 mt-1">
              Set up rules that must be true for this automation to continue
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-sm font-medium">Condition Type</Label>
          <Select value={selectedGroup || ''} onValueChange={onChangeGroup}>
            <SelectTrigger>
              <SelectValue placeholder="Select condition type..." />
            </SelectTrigger>
            <SelectContent>
              {groupsForVariable.map((group) => (
                <SelectItem key={group} value={group}>
                  {convertToTitleCase(group)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedGroup && (
          <div className="space-y-2">
            <Select value={selectedFieldId || ''} onValueChange={onChangeField}>
              <SelectTrigger>
                <SelectValue placeholder="Select field..." />
              </SelectTrigger>
              <SelectContent>
                {fieldsForGroup.map((field) => (
                  <SelectItem key={field.id} value={field.id}>
                    {convertToTitleCase(field.displayName)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {selectedFieldId && (
          <div className="space-y-2">
            <Select value={selectedOperator || ''} onValueChange={onChangeOperator}>
              <SelectTrigger>
                <SelectValue placeholder="Select operator..." />
              </SelectTrigger>
              <SelectContent>
                {operators.map((operator) => (
                  <SelectItem key={operator.value} value={operator.value}>
                    {operator.operatorLabel}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {selectedOperator && (
          <div className="space-y-2">
            {renderValueInput()}
          </div>
        )}

        {validationError && (
          <div className="text-sm text-red-500">
            {validationError}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t border-grey-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {!isNewCondition && (
            <Button
              variant="outline"
              className="text-red-500 hover:text-red-600"
              onClick={() => {
                const updatedChildren = existingConditions.filter((c: AutomationConditionV2) => c.uuid !== currentCondition?.uuid);
                onUpdateNode(selectedNode.id, {
                  metadata: {
                    ...selectedNode.metadata,
                    children: updatedChildren,
                    condition: updatedChildren.length > 0 ? updatedChildren[0] : null,
                  },
                });
                onClose();
              }}
            >
              Delete
            </Button>
          )}
          <Button onClick={handleDone}>
            {isNewCondition ? 'Add Condition' : 'Update Condition'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
